import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInsight } from "./InsightContext";
import {
    getInsight,
    getInsightTitle,
    prefetchInsight,
    loadInsightDirectory,
    getCachedDirectory,
    subscribeRegistry,
} from "./registry";
import { resolveRouteInsight } from "./routeMap";
import "./insight.css";

/**
 * InsightOverlay — the single global UI layer for Insight Mode.
 *
 * Responsibilities (and ONLY these):
 *   - Render the veil, status bar, hover ring, and rail.
 *   - Intercept clicks at the document capture phase while active.
 *   - Track hovered registered elements via a single absolutely-positioned ring.
 *
 * Non-responsibilities (deliberate):
 *   - Mutating routes, scroll, or any underlying state.
 *   - Wrapping pages or components.
 *   - Owning content. Content lives in the registry.
 */

const InsightOverlay = () => {
    const { t } = useTranslation();
    const SECTION_ORDER = useMemo(() => [
        { key: "overview", label: t("insight.tabs.overview") },
        { key: "how_it_works", label: t("insight.tabs.how_it_works") },
        { key: "why_it_matters", label: t("insight.tabs.why_it_matters") },
        { key: "pro_tips", label: t("insight.tabs.pro_tips") },
    ], [t]);
    const { active, target, locale, open, close, firstRunSeen, markFirstRunSeen } =
        useInsight();
    const [section, setSection] = useState("overview");
    const [swapping, setSwapping] = useState(false);
    const [hoverRect, setHoverRect] = useState(null);
    const [, setTick] = useState(0);
    const tickRef = useRef(0);
    const rafRef = useRef(0);
    const lastHoveredRef = useRef(null);
    /* Source label of the element that opened the current rail entry —
     * shown in the rail as a calm "About: X" tag so the operator sees the
     * cause-and-effect link between page and rail at all times. */
    const [sourceLabel, setSourceLabel] = useState(null);
    /* Transient status-bar message shown when an operator taps a
     * non-teachable element. Clears itself after a short delay. */
    const [statusFlash, setStatusFlash] = useState(null);
    /* True until the operator taps their first teachable element. Drives
     * the "waiting" rail state and the primary-element pulse persistence. */
    const [awaitingFirstTap, setAwaitingFirstTap] = useState(true);

    /* ── Subscribe to async registry fills (route cache → re-render) ── */
    useEffect(() => {
        const unsub = subscribeRegistry(() => {
            tickRef.current += 1;
            setTick(tickRef.current);
        });
        return () => unsub();
    }, []);

    /* ── Reset section when target changes (with a soft 120ms cross-fade) ── */
    useEffect(() => {
        if (!target) return undefined;
        setSwapping(true);
        const t = setTimeout(() => {
            setSection("overview");
            setSwapping(false);
        }, 130);
        return () => clearTimeout(t);
    }, [target]);

    /* ── Body class so registered elements rise above the veil ── */
    useEffect(() => {
        if (active) document.body.classList.add("insight-active");
        else document.body.classList.remove("insight-active");
        return () => document.body.classList.remove("insight-active");
    }, [active]);

    /* ── Activation: prepare the page, do NOT preempt the operator ──
     *
     * On activation, three things happen — in this order, on purpose:
     *
     *   1. Every instrumented element on the visible page is batch-
     *      prefetched into the registry cache so a tap renders in <120ms.
     *
     *   2. The first registered element on the page is promoted to
     *      `data-insight-primary` if no element was pre-marked. This
     *      gives the eye a confident "start here" anchor via the
     *      primary-pulse halo (CSS).
     *
     *   3. The rail is NOT auto-opened when the page has teachable
     *      elements. Auto-opening reads as "we picked the answer for
     *      you" — the opposite of "tap anything to learn about it." The
     *      empty rail with a calm prompt invites real interaction.
     *
     *      Fallback: if the page has zero registered elements (Tier 1
     *      route-baseline pages), THEN we auto-open the route's entry
     *      so the operator gets at least page-level orientation.
     */
    const location = useLocation();
    const promotedRef = useRef(null);
    useEffect(() => {
        if (!active) return;
        const t = setTimeout(() => {
            const allTargets = Array.from(
                document.querySelectorAll("[data-insight-id]")
            );
            const ids = Array.from(
                new Set(
                    allTargets
                        .map((el) => el.getAttribute("data-insight-id"))
                        .filter(Boolean)
                )
            );
            ids.forEach((id) => prefetchInsight(id, locale));

            // Promote a primary element if none was pre-marked. Pick the
            // first registered element in DOM order (top-of-page bias).
            const existingPrimary = document.querySelector(
                '[data-insight-primary="true"][data-insight-id]'
            );
            if (!existingPrimary && allTargets[0]) {
                allTargets[0].setAttribute("data-insight-primary", "true");
                promotedRef.current = allTargets[0];
            }

            // Page has zero teachable elements → fallback to route entry.
            if (allTargets.length === 0 && !target) {
                const routeKey = resolveRouteInsight(location.pathname);
                if (routeKey) {
                    prefetchInsight(routeKey, locale);
                    open(routeKey);
                }
            }
        }, 80);
        return () => clearTimeout(t);
    }, [active, target, open, location.pathname, locale]);

    /* Clean up when Help Mode closes — demote any promoted primary and
     * reset the transient state so the next activation starts fresh. */
    useEffect(() => {
        if (!active) {
            if (promotedRef.current) {
                promotedRef.current.removeAttribute("data-insight-primary");
                promotedRef.current = null;
            }
            setSourceLabel(null);
            setStatusFlash(null);
            setAwaitingFirstTap(true);
        }
    }, [active]);

    /* ── Capture-phase click interceptor ── */
    /* Pull a short, human label from an element. Used in the rail's
     * "About: X" source line so the operator sees what they tapped. */
    const labelFromElement = useCallback((el) => {
        if (!el) return null;
        // Prefer explicit author intent if provided.
        const explicit = el.getAttribute("data-insight-label");
        if (explicit) return explicit.trim().slice(0, 60);
        // aria-label is the next best signal.
        const aria = el.getAttribute("aria-label");
        if (aria) return aria.trim().slice(0, 60);
        // Finally, fall back to short trimmed text content.
        const text = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ");
        if (text) return text.slice(0, 60);
        return null;
    }, []);

    /* Calm tap-feedback for non-teachable elements. Renders a brief
     * ripple at the tap point and shows a transient status-bar message
     * so the operator never feels like the page is frozen/dead. */
    const flashRipple = useCallback((x, y) => {
        if (typeof document === "undefined") return;
        const node = document.createElement("div");
        node.className = "insight-ripple";
        node.style.left = `${x}px`;
        node.style.top = `${y}px`;
        document.body.appendChild(node);
        window.setTimeout(() => { node.remove(); }, 560);
    }, []);

    const flashStatus = useCallback((msg, ms = 1800) => {
        setStatusFlash(msg);
        window.setTimeout(() => {
            setStatusFlash((cur) => (cur === msg ? null : cur));
        }, ms);
    }, []);

    const handleClickCapture = useCallback(
        (e) => {
            // Allow interaction with the insight system itself.
            const inSystem = e.target.closest?.("[data-insight-system]");
            if (inSystem) return;

            // Registered element → open its panel, swallow original click,
            // record the source label for the rail's "About: X" line.
            const hit = e.target.closest?.("[data-insight-id]");
            if (hit) {
                e.preventDefault();
                e.stopPropagation();
                const id = hit.getAttribute("data-insight-id");
                if (id) {
                    setSourceLabel(labelFromElement(hit));
                    setAwaitingFirstTap(false);
                    // Mark this element as the current rail source for the
                    // bright "current" halo (CSS).
                    document.querySelectorAll('[data-insight-current="true"]')
                        .forEach((n) => n.removeAttribute("data-insight-current"));
                    hit.setAttribute("data-insight-current", "true");
                    open(id);
                }
                return;
            }

            // Non-teachable tap — calm feedback so the page never feels dead.
            e.preventDefault();
            e.stopPropagation();
            // Only on the click event (not mousedown) so we don't double-fire.
            if (e.type === "click") {
                const cx = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
                const cy = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
                if (cx && cy) flashRipple(cx, cy);
                flashStatus(t("insight.rail.not_teachable"));
            }
        },
        [open, labelFromElement, flashRipple, flashStatus]
    );

    useEffect(() => {
        if (!active) return undefined;
        // Use capture phase + both click & mousedown to fully freeze the page.
        document.addEventListener("click", handleClickCapture, true);
        document.addEventListener("mousedown", handleClickCapture, true);
        return () => {
            document.removeEventListener("click", handleClickCapture, true);
            document.removeEventListener("mousedown", handleClickCapture, true);
        };
    }, [active, handleClickCapture]);

    /* ── Hover ring tracking (single node, RAF-throttled) ── */
    const updateRing = useCallback((target) => {
        if (!target) {
            setHoverRect(null);
            return;
        }
        const rect = target.getBoundingClientRect();
        // Add a tiny breathing margin.
        const pad = 6;
        setHoverRect({
            x: rect.left - pad,
            y: rect.top - pad,
            w: rect.width + pad * 2,
            h: rect.height + pad * 2,
        });
    }, []);

    useEffect(() => {
        if (!active) {
            setHoverRect(null);
            return undefined;
        }
        const onMove = (e) => {
            const el = e.target.closest?.("[data-insight-id]");
            if (el === lastHoveredRef.current) return;
            lastHoveredRef.current = el;
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => updateRing(el));
        };
        const onScrollOrResize = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() =>
                updateRing(lastHoveredRef.current)
            );
        };
        document.addEventListener("mousemove", onMove, true);
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            document.removeEventListener("mousemove", onMove, true);
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
            cancelAnimationFrame(rafRef.current);
            lastHoveredRef.current = null;
        };
    }, [active, updateRing]);

    /* ── Resolve the entry to render ──
     * Bind to tickRef so re-renders triggered by cache fills recompute
     * the entry (useMemo on [target, locale] alone would return stale null
     * because neither dep changes when the API response arrives).
     */
    const entry = target ? getInsight(target, locale) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tickRef.current intentionally read
    const _entryTick = tickRef.current;

    /* ── Directory entries (shown only when no target is set) ── */
    const directory = active && !target ? getCachedDirectory(locale) : [];

    /* ── Related chip click (cross-fade within rail) ── */
    const handleRelated = useCallback(
        (id) => {
            setSwapping(true);
            setTimeout(() => {
                open(id);
            }, 120);
        },
        [open]
    );

    /* ── First-run is no longer a blocking welcome card. ──
     * We auto-anchor content immediately. The status bar conveys the
     * only instruction the operator needs. We still mark the first run
     * as "seen" the first time the user enters Insight Mode, in case
     * we want to surface a tiny one-time hint later.
     */
    useEffect(() => {
        if (active && !firstRunSeen) markFirstRunSeen();
    }, [active, firstRunSeen, markFirstRunSeen]);

    /* Detect touch device for the "Exit · Esc" → "Exit Help Mode" copy.
     * Esc means nothing on an iPad. We pick once on mount and stick with
     * it — re-evaluating on every render would feel flickery. */
    const [isTouch] = useState(() => {
        if (typeof window === "undefined") return false;
        return (
            "ontouchstart" in window ||
            (navigator?.maxTouchPoints || 0) > 0
        );
    });

    /* Clear the "current source" highlight whenever the rail target is
     * cleared (close/exit). Chip traversals deliberately KEEP the source
     * highlight on the original page element — the operator should never
     * lose the visual thread back to where their journey started. */
    useEffect(() => {
        if (!target) {
            document.querySelectorAll('[data-insight-current="true"]').forEach(
                (n) => n.removeAttribute("data-insight-current")
            );
        }
    }, [target]);

    const sectionText = entry?.sections?.[section] || "";

    /* Page has teachable elements waiting? Used to render the calm
     * "waiting" rail state on activation instead of an empty rail or an
     * auto-anchored entry. */
    const [pageHasTeachables, setPageHasTeachables] = useState(false);
    useEffect(() => {
        if (!active) {
            setPageHasTeachables(false);
            return;
        }
        const check = () => {
            setPageHasTeachables(
                document.querySelectorAll("[data-insight-id]").length > 0
            );
        };
        const t = setTimeout(check, 100);
        return () => clearTimeout(t);
    }, [active, location.pathname]);

    return createPortal(
        <>
            {/* Veil */}
            <div className="insight-veil" data-active={active ? "true" : "false"} aria-hidden="true" />

            {/* Hover ring — desktop affordance only. Touch devices rely on
                the persistent breathing halo (CSS) for tap affordance. */}
            {active && !isTouch && hoverRect && (
                <div
                    className="insight-hover-ring"
                    data-visible="true"
                    aria-hidden="true"
                    style={{
                        transform: `translate3d(${hoverRect.x}px, ${hoverRect.y}px, 0)`,
                        width: hoverRect.w,
                        height: hoverRect.h,
                    }}
                />
            )}

            {/* Status bar — active voice. Flashes a calm message when the
                operator taps a non-teachable element so the page never
                feels dead. */}
            <div
                className="insight-statusbar"
                data-active={active ? "true" : "false"}
                data-insight-system="statusbar"
                role="status"
                aria-live="polite"
            >
                <span className="insight-statusbar__dot" />
                <span data-testid="insight-status-text">
                    {statusFlash || (
                        pageHasTeachables
                            ? t("insight.statusbar.on_no_target")
                            : t("insight.statusbar.on_with_target")
                    )}
                </span>
                <button
                    type="button"
                    className="insight-statusbar__exit"
                    onClick={close}
                    data-insight-system="exit"
                    data-testid="insight-exit"
                >
                    {isTouch ? t("insight.statusbar.exit") : `${t("insight.statusbar.exit_short")} · ${t("insight.statusbar.exit_kbd")}`}
                </button>
            </div>

            {/* Rail */}
            <aside
                className="insight-rail"
                data-visible={active ? "true" : "false"}
                data-insight-system="rail"
                data-testid="insight-rail"
                aria-label={t("insight.rail.aria_label")}
            >
                {entry && (
                    <>
                        <header className="insight-rail__header" data-testid="insight-rail-header">
                            {sourceLabel && (
                                <div className="insight-rail__source" data-testid="insight-rail-source">
                                    <span className="insight-rail__source-dot" aria-hidden="true" />
                                    <span className="insight-rail__source-label">
                                        {t("insight.rail.about_prefix")}: {sourceLabel}
                                    </span>
                                </div>
                            )}
                            <div className="insight-rail__eyebrow">{entry.eyebrow}</div>
                            <div className="insight-rail__title" data-testid="insight-rail-title">
                                {entry.title}
                            </div>
                            {entry.resolved_lang &&
                             entry.resolved_lang !== "en" &&
                             entry.translation_status &&
                             entry.translation_status !== "complete" &&
                             entry.translation_status !== "n/a" && (
                                <div
                                    className="insight-rail__fallback-chip"
                                    title={t("insight.rail.en_fallback_tooltip")}
                                    data-testid="insight-rail-en-fallback-chip"
                                >
                                    {t("insight.rail.en_fallback_chip")}
                                </div>
                            )}
                        </header>

                        <div className="insight-rail__tabs" role="tablist">
                            {SECTION_ORDER.map((s) => (
                                <button
                                    key={s.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={section === s.key}
                                    data-active={section === s.key ? "true" : "false"}
                                    data-insight-system="tab"
                                    data-testid={`insight-tab-${s.key}`}
                                    className="insight-rail__tab"
                                    onClick={() => setSection(s.key)}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        <div
                            className="insight-rail__body"
                            data-swapping={swapping ? "true" : "false"}
                            data-testid="insight-rail-body"
                        >
                            <p>{sectionText}</p>
                        </div>

                        {entry.related?.length > 0 && (() => {
                            const resolved = entry.related
                                .map((rid) => ({ rid, title: getInsightTitle(rid, locale) }))
                                .filter((r) => !!r.title);
                            if (resolved.length === 0) return null;
                            return (
                                <div className="insight-rail__related">
                                    <div className="insight-rail__related-label">Related</div>
                                    {resolved.map(({ rid, title }) => (
                                        <button
                                            key={rid}
                                            type="button"
                                            className="insight-rail__chip"
                                            data-insight-system="related-chip"
                                            data-testid={`insight-related-${rid}`}
                                            onClick={() => handleRelated(rid)}
                                        >
                                            {title}
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}
                    </>
                )}

                {/* Waiting state — the rail is open but empty on purpose.
                    The page is freshly lit and the operator's first tap
                    decides what content arrives. This is the heart of the
                    "tap anything to learn about it" promise. */}
                {!entry && active && pageHasTeachables && (
                    <div className="insight-rail__waiting" data-testid="insight-waiting">
                        <div className="insight-rail__waiting-eyebrow">{t("insight.rail.empty_eyebrow")}</div>
                        <div className="insight-rail__waiting-title">
                            {t("insight.rail.empty_body")}
                        </div>
                        <div className="insight-rail__waiting-arrow" aria-hidden="true">
                            ←
                        </div>
                    </div>
                )}

                {/* Directory — fallback for pages that have no registered
                    targets yet. Calm, listy, tappable. */}
                {!entry && active && !pageHasTeachables && (
                    <div className="insight-rail__directory" data-testid="insight-directory">
                        <header className="insight-rail__header">
                            <div className="insight-rail__eyebrow">Help &amp; Insights</div>
                            <div className="insight-rail__title">What would you like to understand?</div>
                        </header>
                        <div className="insight-rail__body">
                            <div className="insight-rail__dirlist">
                                {directory.map((d) => (
                                    <button
                                        key={d.id}
                                        type="button"
                                        className="insight-rail__diritem"
                                        data-insight-system="dir-item"
                                        data-testid={`insight-dir-${d.id}`}
                                        onClick={() => handleRelated(d.id)}
                                    >
                                        <span className="insight-rail__diritem-eyebrow">{d.eyebrow}</span>
                                        <span className="insight-rail__diritem-title">{d.title}</span>
                                        {d.overview && (
                                            <span className="insight-rail__diritem-overview">{d.overview}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        </>,
        document.body
    );
};

export default InsightOverlay;
